import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import VideoStream from "@components/operatorLobby/VideoStream";
import ControlStream from "@components/operatorLobby/ControlStream";
import TelemetryStream from "@components/operatorLobby/telemetry/TelemetryStream";
import PingStream from "@components/operatorLobby/PingStream";
import TimeoutStream from "@components/operatorLobby/TimeoutStream";
import useWebRTC from "@components/operatorLobby/useWebRTC";
import useActivityTimer from "./useActivityTimer";
import Sound from "@shared/Sound/Sound";
import activityTimerSound from "@public/sound/afk_1_long.wav";
import emergencySound from "@public/sound/emergency_2.wav";
import Maintenance from "@components/operatorLobby/operations/Maintenance";
import Allocations from "./operations/Allocations";
import PathDrawing from "@components/operatorLobby/PathDrawing/PathDrawing";

const TAB = {
  TELEMETRY: "Telemetry",
  DRAWING: "Drawing",

  //MAINTENANCE: "Maintenance",
  //ALLOCATIONS: "Allocations",
};

// vehicle cannot be null
const ActiveConnection = (
  {
    vehicle,
    sendToInfrastructure,
    controlUpdateSetters,
    controlSendSetters,
    vehicleStateRequestSetters,
    interruptStreams,
    isMobile,
    voiceCallState,
    listOfAvailableVehicles,
  },
  ref
) => {
  const [streams, setStreams] = useState([]);
  const [dataChannels, setDataChannels] = useState({});
  const [currentTab, setCurrentTab] = useState(TAB.TELEMETRY);
  const scrollTarget = useRef(null);
  //const [prerecordedPath, setPrerecordedPath] = useState([]);
  //const [isConnecting, setConnecting] = useState(false);
  //const [isConnected, setConnected] = useState(false);

  const [mechanicalState, setMechanicalState] = useState({
    steeringWheel: 0,
    frontWheelsAngle: 0,
    rearWheelsAngle: 0,
    wheelMode: 2,
    gear: 2,
  });




  function onTrack(event) {
    const stream = event.streams[0];
    setStreams((previousStreams) => previousStreams.concat([stream]));
  }

  function onDataChannel(event) {
    const dataChannel = event.channel;

    dataChannel.onopen = function () {
      console.log(`${dataChannel.label} stream open`);
      setDataChannels((channels) => {
        channels[dataChannel.label] = dataChannel;
        return channels;
      });
    };

    dataChannel.onclose = function () {
      console.log(`${dataChannel.label} stream close`);
      interruptStreams();
    };
  }

  function answer(sdp) {
    return sendToInfrastructure(vehicle.id, "streamAnswer", sdp);
  }

  function sendIce(candidate) {
    return sendToInfrastructure(vehicle.id, "streamIceCandidate", candidate);
  }

  function onOpened() {
    // does not work on firefox for some reason
    //setConnecting(false);
  }

  function onClosed() {
    interruptStreams();
  }

  const { handleOffer } = useWebRTC({
    onTrack,
    onDataChannel,
    answer,
    sendIce,
    onOpened,
    onClosed,
  });

  useImperativeHandle(ref, () => ({
    onInfrastructureMessage: function (message) {
      //const from = message.from;
      switch (message.type) {
        case "streamOffer":
          handleOffer(message.content);
          break;
        /*case "preRecordedPath":
          setPrerecordedPath(message.content);
          break;*/
        default:
          console.error(`Unhandled case, message of type: ${message.type}`);
      }
    },
  }));

  useEffect(() => {
    if (sendToInfrastructure) {
      console.log(`Sending stream request to vehicle ${vehicle.id}`);
      if (isMobile) {
        sendToInfrastructure(vehicle.id, "streamRequest", [
          { type: "data", name: "telemetry" },
          { type: "data", name: "alert" },
        ]);
      } else {
        sendToInfrastructure(vehicle.id, "streamRequest", [
          { type: "video", name: "front" },
          { type: "video", name: "left" },
          { type: "video", name: "right" },
          { type: "video", name: "back" },
          { type: "data", name: "control" },
          { type: "data", name: "ping" },
          { type: "data", name: "timeout" },
          { type: "data", name: "telemetry" },
        ]);
      }

      return () => {
        setStreams([]);
        setDataChannels({});
      };
    }
  }, [vehicle.id, sendToInfrastructure, isMobile]);

  useEffect(() => {
    vehicleStateRequestSetters.forEach((setVehicleStateRequest) => {
      console.log("setting vehicle state request");
      const prefilledWithIdCallback = (requestedVehicleState) =>
        sendToInfrastructure(vehicle.id, "updateVehicleStateRequest", {
          state: requestedVehicleState,
        });
      setVehicleStateRequest(() => prefilledWithIdCallback); // need to wrap into a parameterless function to handle lazy initialization of useState
    });

    return () => {
      vehicleStateRequestSetters.forEach((setVehicleStateRequest) => {
        console.log("removing vehicle state request");
        setVehicleStateRequest(null);
      });
    };
  }, [vehicle.id, vehicleStateRequestSetters, sendToInfrastructure]);

  const activityTimer = useActivityTimer(
    vehicle.state === "driving" && !isMobile,
    vehicle.id,
    sendToInfrastructure
  );

  const displayShutdownOverlay = vehicle.state === "shuttingDown";
  const displayEmergencyOverlay = vehicle.state === "emergency";

  const videoStreams = streams.map((stream) => {
    const props = {
      interrupt: interruptStreams,
      stream,
      displayShutdownOverlay,
      displayEmergencyOverlay,
      activityTimer,
      mechanicalState,
    };
    return <VideoStream key={stream.id} {...props} />;
  });

  // This control updater enables telemtry information to update the control object in control stream.
  const [controlUpdate, setControlUpdate] = useState(null);
  controlUpdateSetters = useMemo(
    () => [...controlUpdateSetters, setControlUpdate],
    [controlUpdateSetters, setControlUpdate]
  );

  const dataStreams = [];

  if (dataChannels.control) {
    const props = {
      controlUpdateSetters,
      controlSendSetters,
      activityTimerRefresh: activityTimer.refresh,
    };
    const component = (
      <ControlStream key={"control"} dataChannel={dataChannels.control} {...props} />
    );
    dataStreams.push(component);
  }

  if ((dataChannels.telemetry || dataChannels.alert) && currentTab === TAB.TELEMETRY) {
    const component = (
      <TelemetryStream
        key={"telemetry"}
        telemetryDataChannel={dataChannels.telemetry}
        alertDataChannel={dataChannels.alert}
        hidden={!isMobile}
        vehicle={vehicle}
        controlUpdate={controlUpdate}
        setMechanicalState={setMechanicalState}
        sendToInfrastructure={sendToInfrastructure}
        voiceCallState={voiceCallState}
        listOfAvailableVehicles={listOfAvailableVehicles}
      />
      // null
    );
    dataStreams.push(component);

    if (dataChannels.ping) {
      const component = <PingStream key={"ping"} dataChannel={dataChannels.ping} />;
      dataStreams.push(component);
    }

    if (dataChannels.timeout) {
      const component = <TimeoutStream key={"timeout"} dataChannel={dataChannels.timeout} />;
      dataStreams.push(component);
    }
  }

  function onScrollReady() {
    // if (scrollTarget.current) {
    //   scrollTarget.current.scrollIntoView();
    // }
  }

  if (currentTab === TAB.MAINTENANCE) {
    dataStreams.push(
      <Maintenance key="maintenance" vehicle={vehicle} onScrollReady={onScrollReady} />
    );
  }

  if (currentTab === TAB.ALLOCATIONS) {
    dataStreams.push(
      <Allocations key="allocations" vehicle={vehicle} onScrollReady={onScrollReady} />
    );
  }
  if (currentTab === TAB.DRAWING) {
    dataStreams.push(
      <PathDrawing vehicle={vehicle} sendToInfrastructure={sendToInfrastructure} />
    );
  }


  function switchTab(target) {
    setCurrentTab(target);
  }

  return (
    <div className="row">
      {videoStreams}
      {vehicle.state === "emergency" ? <Sound path={emergencySound} /> : null}
      {activityTimer.level === 3 && <Sound path={activityTimerSound} />}
      <div className="col-4 text-center p-2"></div>
      <div className="col-4 text-center p-2">
        <h4>Vehicle: {vehicle.id}</h4>
        <button className="col btn btn-outline-primary" onClick={interruptStreams}>
          Stop driving
        </button>
      </div>
      <div className="col-4 text-center p-2">
        <h4>State: {vehicle.state}</h4>
        {/*voiceCallState?.vuiOfVehicles?.[vehicle.id] ? null : (
          <div style={{ color: "orange", fontWeight: "bold" }}>Warning: VUI not connected</div>
        )*/ /*Only used for LOXO*/}
      </div>
      <div className="col-8 text-center p-2" ref={scrollTarget}>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }}>
          <ul className="nav nav-tabs">
            {Object.values(TAB).map((title) => {
              return (
                <li key={title} className="nav-item" style={{ cursor: "pointer" }}>
                  <button
                    className={"nav-link" + (title === currentTab ? " active" : "")}
                    onClick={() => switchTab(title)}
                  >
                    {title}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      {dataStreams}
    </div>
  );
};

export default forwardRef(ActiveConnection);
